'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';

import SocialMediaLinks from '../shared/SocialMediaIcons';
import SubscribeWidget from '../subscribeWidget/SubscribeWidget';

import { scrollToTop } from '@/utils/helpful';
import classNames from 'classnames';
import Logo from '@/app/components/shared/Logo';
import PoweredBySerefin from '@/app/components/shared/PoweredBySerefin';
import NavLinkList from '@/app/components/shared/NavLinkList';
import { getPrimaryLinks, getResourceLinks } from '@/navigation/links';
import CopyrightNotice from '@/app/components/shared/CopyrightNotice';

const Footer = ({
  isFooterBackgroundWhite,
  isFooterContentWhite,
}: {
  isFooterContentWhite?: boolean;
  isFooterBackgroundWhite?: boolean;
}) => {
  const footerLinks = [
    ...getPrimaryLinks().filter(
      l => l.id === 'our-story' || l.id === 'faq' || l.id === 'our-network'
    ),
    ...getResourceLinks().filter(
      l => l.id === 'sti-info' || l.id === 'resources'
    ),
  ];

  return (
    <section
      className={classNames(
        'flex w-full flex-col items-center justify-center px-8 py-[48px] md:py-[24px]',
        { 'text-white': isFooterContentWhite },
        { 'bg-white': isFooterBackgroundWhite },
        { 'bg-transparent': !isFooterBackgroundWhite }
      )}
    >
      <div className="content-wrapper flex w-full flex-col items-center justify-between">
        <div className="flex w-full items-center justify-between max-md:w-full max-md:max-w-none">
          <Logo
            variant={isFooterContentWhite ? 'white' : 'gradient'}
            height={38}
            width={110}
            sizes="110px"
            priority
          />
          <Image
            src="/arrow-up-gradient-icon.svg"
            className="h-6 w-6 cursor-pointer"
            alt="Scroll to top"
            width={24}
            height={24}
            onClick={() => scrollToTop()}
          />
        </div>
        <div className="mt-10 grid w-full grid-cols-1 gap-2 md:grid-cols-1 lg:grid-cols-2">
          <div className="flex flex-col">
            <div className="flex">
              <div className="block flex gap-20 max-sm:hidden">
                <NavLinkList
                  items={footerLinks}
                  className="flex flex-col gap-3"
                  linkClassName="font-['Karla'] font-normal hover:underline"
                />
              </div>

              <div className="flex flex-col">
                <span className="mb-6 font-['Spectral'] text-xl">
                  Contact Us
                </span>
                <a className="hover:underline" href="mailto:contact@trusti.ca">
                  contact@trusti.ca
                </a>
                <a className="hover:underline" href="tel:6479519069">
                  647-951-9069
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <div className="flex flex-col">
              <span className="mb-6 font-['Spectral'] text-xl">Follow Us</span>
              <SocialMediaLinks isFooterContentWhite={isFooterContentWhite} />
            </div>
          </div>

          <div className="flex flex-col justify-end">
            <PoweredBySerefin
              isWhite={Boolean(isFooterContentWhite)}
              display="desktopOnly"
            />
          </div>
          <div className="flex flex-col">
            <div className="flex flex-col" id="subscribe">
              <span className="mb-6 font-['Spectral'] text-xl font-semibold">
                Subscribe to Our Newsletter
              </span>
              <SubscribeWidget />
            </div>

            <PoweredBySerefin
              isWhite={Boolean(isFooterContentWhite)}
              display="mobileOnly"
            />

            <div className="mt-4 flex gap-2 max-md:flex-col max-md:items-center max-md:justify-center">
              <div className="flex flex-col justify-end font-['Karla'] text-[15px] font-light max-md:text-xs max-md:leading-[15px] max-md:font-normal">
                <CopyrightNotice className="uppercase" />
              </div>

              <div className="flex flex-col justify-end font-['Karla'] text-[15px] font-light max-md:text-xs max-md:leading-[15px] max-md:font-normal">
                <div className="flex gap-2">
                  <Link
                    href="/terms-of-use"
                    className="uppercase hover:underline"
                  >
                    Terms Of Use
                  </Link>
                  |
                  <Link
                    href="/privacy-policy"
                    className="uppercase hover:underline"
                  >
                    Privacy Policy
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Footer;
